import { useState } from 'react';
import { CalendarOff, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { dayLabel, dayTone, fmtDay, metaFor, prettyMinutes } from '@/lib/plannerUtils';

/** The whole run, month by month — every day expandable, nothing hidden. */
export function RoadmapTab({ plan, onToggleDone, onNotToday, canManage }) {
  const [open, setOpen] = useState({});
  const days = plan?.days || [];

  const months = days.reduce((acc, day) => {
    const key = fmtDay(day.date, { month: 'long', year: 'numeric' });
    (acc[key] = acc[key] || []).push(day);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="planner-roadmap">
      {!days.length && (
        <p className="text-sm text-slate-500 font-body" data-testid="planner-roadmap-empty">
          No plan yet — build it from the Exams page and the full roadmap appears here.
        </p>
      )}
      {Object.entries(months).map(([month, monthDays]) => (
        <section key={month} data-testid={`planner-month-${month.replace(/\s/g, '-')}`}>
          <h3 className="font-heading text-sm md:text-lg font-bold text-slate-900">{month}</h3>
          <p className="font-body text-[11px] text-slate-400 mb-2">
            {monthDays.length} planned days · {prettyMinutes(monthDays.reduce((t, d) => t + d.minutes, 0))}
          </p>
          <div className="space-y-1.5">
            {monthDays.map(day => {
              const isOpen = open[day.date] ?? day.date === plan?.today;
              const done = day.items.filter(i => i.done).length;
              return (
                <div key={day.date} className={`rounded-2xl border bg-white ${dayTone(day)} ${day.date === plan?.today ? 'ring-2 ring-indigo-300' : ''}`}
                  data-testid={`planner-roadmap-day-${day.date}`}>
                  <button onClick={() => setOpen(o => ({ ...o, [day.date]: !isOpen }))}
                    data-testid={`planner-roadmap-toggle-${day.date}`}
                    className="w-full flex items-center gap-2 p-3 text-left">
                    {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                    <span className="font-heading text-[13px] text-slate-800 flex-shrink-0">
                      {fmtDay(day.date)}{day.date === plan?.today && ' · today'}
                    </span>
                    <span className="font-body text-[11px] text-slate-400 flex-1 truncate">
                      {dayLabel(day)} · {prettyMinutes(day.minutes)} · {day.items.length} block{day.items.length === 1 ? '' : 's'}
                    </span>
                    <span className="font-body text-[11px] text-slate-400">{done}/{day.items.length}</span>
                  </button>
                  {isOpen && (
                    <div className="px-3 pb-3 space-y-1.5">
                      {day.items.map(item => {
                        const Icon = metaFor(item).icon;
                        return (
                          <div key={item.ref} className="flex items-center gap-2" data-testid={`planner-roadmap-item-${item.ref}`}>
                            <button onClick={() => onToggleDone(item.ref, !item.done)}
                              data-testid={`planner-roadmap-done-${item.ref}`}
                              className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${item.done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300'}`}>
                              {item.done && <Check className="w-3 h-3" />}
                            </button>
                            <Icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                            <p className={`font-body text-[12px] min-w-0 flex-1 ${item.done ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                              <span className="text-slate-400">{item.subject} · </span>{item.label}
                            </p>
                            <span className="font-body text-[10px] text-slate-400 flex-shrink-0">{item.minutes}m</span>
                          </div>
                        );
                      })}
                      {canManage && (
                        <button onClick={() => onNotToday(day.date)} data-testid={`planner-not-today-${day.date}`}
                          className="inline-flex items-center gap-1 text-[11px] font-heading text-slate-400 hover:text-slate-600">
                          <CalendarOff className="w-3 h-3" /> Not this day — spread it forward
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

export default RoadmapTab;
